import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { BotKpis, KPI_FORMULAS } from '../lib/botPage';
import { CARD_LIFT, FONT, KPI_LABEL, RADIUS, TEAL } from '../lib/brand';

const Spark = ({ points }: { points: { date: string; new_recommendations: number }[] }) => {
  if (points.length < 2) return null;
  const max = Math.max(...points.map((p) => p.new_recommendations), 1);
  const w = 90; const h = 22;
  const xy = points.map((p, i) => `${(i / (points.length - 1)) * w},${h - (p.new_recommendations / max) * (h - 3) - 1}`).join(' ');
  return (
    <svg width={w} height={h} role="img" aria-label={`New recommendations per day, ${points[0].date} to ${points[points.length - 1].date}`} data-testid="sparkline">
      <polyline points={xy} fill="none" stroke={TEAL} strokeWidth="1.6" />
    </svg>
  );
};

const pctText = (v: number | null) => (v == null ? '—' : `${v}%`);

/** The five KPI cards. Percentages with a zero denominator read "—", never 0%. Each `?` states its formula. */
export function KpiStrip({ k, trend }: { k: BotKpis; trend: { date: string; new_recommendations: number }[] }) {
  const cards: { id: string; label: string; value: string; sub: React.ReactNode; formula: string }[] = [
    { id: 'total', label: 'Recommendations', value: String(k.total), sub: <Spark points={trend} />, formula: KPI_FORMULAS.total },
    { id: 'agreed', label: 'C-suite agreed', value: pctText(k.agreed_pct), sub: `${k.agreed} / ${k.reviewed} reviewed · ${k.unreviewed} unreviewed · ${k.alternative} alternative`, formula: KPI_FORMULAS.agreed_pct },
    { id: 'approved', label: 'Approved by Grant', value: pctText(k.approved_pct), sub: `${k.approved} Yes / ${k.total} · ${k.decided} decided`, formula: KPI_FORMULAS.approved_pct },
    { id: 'impl', label: 'Implemented (of approved)', value: pctText(k.implemented_pct), sub: `${k.done} done / ${k.approved} approved`, formula: KPI_FORMULAS.implemented_pct },
    { id: 'impl-total', label: 'Implemented (of total)', value: pctText(k.implemented_total_pct), sub: `${k.done} done / ${k.total}`, formula: KPI_FORMULAS.implemented_total_pct },
  ];
  return (
    <Box data-testid="kpi-strip" sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(5, minmax(0,1fr))' }, gap: 1.2, mb: 2 }}>
      {cards.map((c) => (
        <Box key={c.id} data-testid={`bkpi-${c.id}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: RADIUS.lg, p: 1.4, bgcolor: 'background.paper', minWidth: 0, ...CARD_LIFT }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ ...KPI_LABEL, flex: 1, minWidth: 0 }}>{c.label}</Typography>
            <Tooltip title={c.formula} arrow>
              <Box component="button" type="button" aria-label={`How ${c.label} is calculated`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '50%', width: 18, height: 18, fontSize: 10.5, lineHeight: 1, bgcolor: 'transparent', color: 'text.secondary', cursor: 'help', p: 0 }}>?</Box>
            </Tooltip>
          </Box>
          <Typography sx={{ fontFamily: FONT.display, fontSize: 26, fontWeight: 700, lineHeight: 1.2, color: 'primary.main' }}>{c.value}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">{c.sub}</Typography>
        </Box>
      ))}
    </Box>
  );
}
